'use client';
import { useEffect, useState, useTransition } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import Header from "../../../../components/Header/Header";
import Footer from "../../../../components/Footer/Footer";
import { Newsletter, SideAds } from "../../../../components/Ads/Ads";
import RelatedArticlesCard from "../components/RelatedArticlesCard";
import SharePostIcons from "../../../../components/SocialIcons/SharePostIcons";
import blogApi from "../../../../services/blogApi";
import Breadcrumb from "../../../../components/Breadcrumb/Breadcrumb";
import { getRelativeTimeString } from "../../../../Utils/datetime";
import ArticlePlaceholder from "../../../../components/Placeholder/Placeholder";
import { toast } from "react-toastify";
import CommentsSection from "../components/CommentsSection";
import "../Blogs.css";
import "./BlogArticle.css";
import ModalBox from "@/components/popupmodel/ModalBox";
import modalStrings from "../../../../config/modalStrings.json";
import { ArticleSideAds } from "@/components/Ads/Articlepage_ads";

const BlogArticlePage = ({ serverPost, cleanHTML, initialCategory }) => {
  const router = useRouter();
  const [isRouteLoading, startRouteTransition] = useTransition();
  const post = serverPost;
  const [submitting, setSubmitting] = useState(false);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [commentData, setCommentData] = useState({
    author_name: "",
    author_email: "",
    content: "",
  });
  const [openModalType, setOpenModalType] = useState(null);

  const handleModalStateChange = (type, isOpen) => {
    setOpenModalType(isOpen ? type : null);
  };

  const handleCommentChange = (e) => {
    const { name, value } = e.target;
    setCommentData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await blogApi.postComment({
        ...commentData,
        post: post.id,
      });
      toast.success("Comment submitted successfully!");
      setCommentData({ author_name: "", author_email: "", content: "" });
    } catch (error) {
      toast.error("Failed to submit comment.");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchRelatedPosts = async () => {
      if (!post?.categories?.[0] || !post?.id) return;

      try {
        const relatedResponse = await blogApi.getRelatedBlogPosts(post.categories[0]);
        const related = Array.isArray(relatedResponse?.data) ? relatedResponse.data : [];

        const formattedRelated = related
          .filter((item) => item?.id !== post.id)
          .slice(0, 4)
          .map((item) => ({
            id: item.id,
            title: item.title?.rendered,
            category: item.categories_details?.[0]?.name || "Other",
            categoryslug: item.categories_details?.[0]?.slug,
            image: item.featured_img_url,
            link: item.link,
            slug: item.slug,
            tag: item.tags_details?.[0]?.name || "Other",
          }));

        setRelatedPosts(formattedRelated);
      } catch (error) {
        console.error("Failed to fetch related blog posts:", error);
        setRelatedPosts([]);
      }
    };

    fetchRelatedPosts();
  }, [post]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [post?.id]);

  const matchedCategory = post?.categories_details?.find(
    (cat) => cat.slug === initialCategory
  );

  const categoryLabel =
    matchedCategory?.name ||
    post?.categories_details?.[0]?.name ||
    "Uncategorized";

  const categorySlug =
    matchedCategory?.slug ||
    post?.categories_details?.[0]?.slug ||
    "";

  const handleRelatedArticleClick = (nextCategorySlug, nextSlug) => {
    if (!nextCategorySlug || !nextSlug) return;
    startRouteTransition(() => {
      router.push(`/blogs/${nextCategorySlug}/${nextSlug}`);
    });
  };

  if (isRouteLoading) {
    return (
      <>
        <Header />
        <ArticlePlaceholder />
        <Footer />
      </>
    );
  }

  if (!post) {
    return (
      <>
        <Header />
        <main className="page-container">
          <h2 className="not-found-msg">Post not found</h2>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="page-container">
        <Breadcrumb
          items={[
            { label: categoryLabel, path: `/blogs/${categorySlug}` },
            { label: post.title.rendered, path: `/blogs/${post.slug}` },
          ]}
        />

        <h1
          className="article-title"
          dangerouslySetInnerHTML={{ __html: post.title.rendered }}
        />

        <p className="Home-text_tag">
          {post.tags_details?.[0]?.name || "Other"}
        </p>

        <div className="article-main">
          <div className="article-left">
            <div className="article-meta">
              <span className="article-metaItem">
                Published On <br />
                <b>
                  <span>{getRelativeTimeString(post.date)}</span>
                </b>
              </span>
              <span className="article-metaItem">
                Written By <br />
                <b>
                  <span>{post.author_details.name}</span>
                </b>
              </span>
              <SharePostIcons blogTitle={post?.title?.rendered} />
            </div>

            <div className="summary-divider" id="summary-divider"></div>

            {post?.jetpack_featured_media_url && (
              <Image
                src={post.jetpack_featured_media_url}
                alt="Main image"
                width={800}
                height={450}
                sizes="(max-width: 1023px) 100vw, 800px"
                quality={72}
                unoptimized={isBlogApiMainImage}
                className="main-image"
                priority
              />
            )}

            <div
              className="article-content"
              dangerouslySetInnerHTML={{ __html: cleanHTML }}
            />

            <SharePostIcons blogTitle={post?.title?.rendered} />

            <div className="comment-form">
              <h2 className="comment-text">Leave a Comment</h2>
              <p className="comment-note">
                Your email address will not be published. Required fields are
                marked
                <span className="text-style-1">*</span>
              </p>

              <form onSubmit={handleSubmit}>
                <div className="form-flex" id="form-flex-blog">
                  <div className="form-group" id="form-group">
                    <label htmlFor="author_name">
                      Name<span className="text-style-1">*</span>
                    </label>
                    <input
                      type="text"
                      name="author_name"
                      placeholder="Name"
                      required
                      value={commentData.author_name}
                      onChange={handleCommentChange}
                    />
                  </div>
                  <div className="form-group" id="form-group">
                    <label htmlFor="author_email">
                      Email<span className="text-style-1">*</span>
                    </label>
                    <input
                      type="email"
                      name="author_email"
                      placeholder="Email"
                      required
                      value={commentData.author_email}
                      onChange={handleCommentChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="content">
                    Comment<span className="text-style-1">*</span>
                  </label>
                  <textarea
                    name="content"
                    placeholder="Type here..."
                    required
                    value={commentData.content}
                    onChange={handleCommentChange}
                  />
                </div>

                <button type="submit" disabled={submitting}>
                  {submitting ? (
                    <span className="loading-content">
                      <span className="spinner" /> Sending...
                    </span>
                  ) : (
                    "Post Comment"
                  )}
                </button>
              </form>
            </div>

            <CommentsSection postId={post.id} />
          </div>

          <div className="article-right">
            <RelatedArticlesCard
              posts={relatedPosts}
              onArticleClick={handleRelatedArticleClick}
            />
            <ArticleSideAds />
            <SideAds />
            <Newsletter />
          </div>
        </div>
      </main>

      <Footer />
      <ModalBox
        type="newsletter"
        noOfTimesToShow={2}
        title={modalStrings.newsletter.title}
        text={modalStrings.newsletter.text}
        buttonText={modalStrings.newsletter.buttonText}
        showInput={true}
        showOnEveryRefresh={true}
        autoShowDelay={1000}
        isAnyModalOpen={openModalType && openModalType !== "newsletter"}
        onModalStateChange={handleModalStateChange}
      />
    </>
  );
};

export default BlogArticlePage;
